import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { get } from "../../api/client.js";
import { styles as tableStyles } from "../../styles/tables.js";
import { styles as sharedStyles } from "../../styles/shared.js";
import { styles as inputStyles } from "../../styles/input.js";
import { date } from "../../formatting/dateformats.js";
import { addDays } from "../../utils.js";
import "../../components/fm-button-v2.js";
import "../../components/fm-progress-dots.js";
import "../../components/fm-form-field.js";
import "../../components/fm-text-input.js";
import "../../components/fm-date-input.js";
import type { DateInputElement } from "../../components/fm-date-input.js";
import type { TextInputElement } from "../../components/fm-text-input.js";

type Transaction = {
	ext_tran_id: unknown;
	custody_code: unknown;
	custody_owner: unknown;
	txttradedate: unknown;
	trantype: unknown;
	amounttype: unknown;
	security_name: unknown;
	quantity: unknown;
	amountpc: unknown;
};

@customElement("transaction-list-view")
export class TransactionListView extends LitElement {
	static styles = [
		sharedStyles,
		tableStyles,
		inputStyles,
		css`
			:host {
				display: block;
				margin: 24px auto;
				max-width: 1400px;
			}

			.card-header {
				justify-content: flex-start;
				gap: 16px;
			}

			.center-padded {
				width: 100%;
				padding: 32px 0;
				display: flex;
				justify-content: center;
			}

			th {
				cursor: pointer;
			}

			.refresh-button {
				align-self: flex-end;
			}
    	`,
	];

	@state()
	private rows: Transaction[] = [];

	private sortedKey: keyof Transaction | null = null;

	@state()
	private loading = false;

	private defaultValues = {
		fromDate: date(addDays(new Date(), -5)),
		toDate: date(new Date()),
	};

	async connectedCallback(): Promise<void> {
		super.connectedCallback();
		this.loading = true;
		await this.fetch({
			fromDate: this.defaultValues.fromDate,
			toDate: this.defaultValues.toDate,
			query: "",
		});
		this.loading = false;
	}

	protected render() {
		return html`
			<div class="card">
				<div class="card-header">
					<h1>Transactions</h1>
				</div>
				<div class="card-header">
					<fm-form-v2 id="filter-form" @submit="${this.onSubmit}">
						<fm-form-field label="From">
							<fm-date-input
								id="from"
								name="from-date"
								.defaultValue="${this.defaultValues.fromDate}"></fm-date-input>
						</fm-form-field>
						<fm-form-field label="To">
							<fm-date-input
								id="to"
								name="to-date"
								.defaultValue="${this.defaultValues.toDate}"></fm-date-input>
						</fm-form-field>
						<fm-form-field label="Search">
							<fm-text-input
								id="search"
								name="search"></fm-text-input>
						</fm-form-field>
						<fm-button-v2 type="submit" class="refresh-button">Refresh</fm-button-v2>
					</fm-form-v2>
				</div>
				<table>
					<thead>
						<tr>
							<th @click="${() => this.sortBy("ext_tran_id")}">ID</th>
							<th @click="${() => this.sortBy("custody_code")}">Custody</th>
							<th @click="${() => this.sortBy("custody_owner")}">Owner</th>
							<th @click="${() => this.sortBy("txttradedate")}">Trade Date</th>
							<th @click="${() => this.sortBy("trantype")}">Type</th>
							<th @click="${() => this.sortBy("security_name")}">Security</th>
							<th @click="${() => this.sortBy("quantity")}" class="numeric">Quantity</th>
							<th @click="${() => this.sortBy("amountpc")}" class="numeric">Amount</th>
							<th @click="${() => this.sortBy("amounttype")}">Amount Type</th>
						</tr>
					</thead>
					<tbody>
						${this.renderTableContent()}
					</tbody>
				</table>
			</div>
    	`;
	}

	private renderTableContent() {
		if (this.loading) {
			return html`
				<tr>
					<td colspan="9">
						<div class="center-padded">
							<fm-progress-dots></fm-progress-dots>
						</div>
					</td>
				</tr>
			`;
		}

		if (this.rows.length === 0) {
			return html`
				<tr>
					<td colspan="9">
						<div class="center-padded">
							No results.
						</div>
					</td>
				</tr>
			`;
		}

		return this.rows.map(
			(row) => html`
					<tr>
						<td>${row.ext_tran_id}</td>
						<td>${row.custody_code}</td>
						<td>${row.custody_owner}</td>
						<td>${row.txttradedate}</td>
						<td>${row.trantype}</td>
						<td>${row.security_name}</td>
						<td class="numeric">${row.quantity}</td>
						<td class="numeric">${row.amountpc}</td>
						<td>${row.amounttype}</td>
					</tr>
			`,
		);
	}

	private findFromDateInput(): DateInputElement | null {
		return this.shadowRoot?.querySelector("#from") ?? null;
	}

	private findToDateInput(): DateInputElement | null {
		return this.shadowRoot?.querySelector("#to") ?? null;
	}

	private findSearchInput(): TextInputElement | null {
		return this.shadowRoot?.querySelector("#search") ?? null;
	}

	private async onSubmit(_event: Event) {
		const fromDate = this.findFromDateInput()?.value ?? "";
		const toDate = this.findToDateInput()?.value ?? "";
		const query = this.findSearchInput()?.value ?? "";

		this.loading = true;
		await this.fetch({
			fromDate,
			toDate,
			query,
		});
		this.loading = false;
	}

	private sortBy(key: keyof Transaction): void {
		if (key === this.sortedKey) {
			this.rows = this.rows.slice(0).reverse();
			return;
		}

		this.sortedKey = key;
		this.rows = this.rows.slice(0).sort((a, b) => {
			const x = a[key];
			const y = b[key];
			if (typeof x === "string" && typeof y === "string") {
				return x.localeCompare(y);
			}

			if (typeof x === "number" && typeof y === "number") {
				return x - y;
			}

			return 0;
		});
	}

	async fetch(values: { fromDate: string; toDate: string; query: string }) {
		const result = await get<{ data: Transaction[] }>(
			`/transactions?fromdate=${values.fromDate}&todate=${values.toDate}&query=${values.query}`,
		);

		if (result.ok === false) {
			return;
		}

		this.rows = result.value.data;
	}
}
